import { createSelector } from "@ngrx/store";
import { locationDetailsWizardFeature } from "src/app/shared/location-details-wizard/location-details-wizard.feature";
import { BuildingType, LocationDetailsWizardOptionValue, LocationDetailsWizardStep, buildingTypes } from "src/app/shared/location-details-wizard/location-details-wizard.state";
import { getSteps, ordinalSuffix } from "src/app/shared/location-details-wizard/location-details-wizard.utils";

export type LocationDetailsWizardOption = {
  value: LocationDetailsWizardOptionValue;
  label: string;
  icon?: string;
  glyph?: string;
  unit?: string;
}

export const selectSteps = createSelector(
  locationDetailsWizardFeature.selectAnswers,
  (answers): LocationDetailsWizardStep[] => getSteps(answers),
);

const buildingTypeIconMap: Record<BuildingType, string> = {
  'Apartment/Condo': 'pi-building',
  'House': 'pi-home',
  'Townhouse': 'pi-home',
  'Duplex': 'pi-home',
  'Commercial': 'pi-shop',
  'Storage Unit': 'pi-warehouse',
  'Office': 'pi-briefcase',
  'Other': 'pi-ellipsis-h',
};

const apartmentCondoSizeOptions: LocationDetailsWizardOption[] = [
  {
    value: 'studio',
    label: 'Studio/Bachelor',
    glyph: 'S',
  },
  {
    value: '1bed',
    label: '1 Bedroom',
    glyph: '1',
  },
  {
    value: '2bed',
    label: '2 Bedroom',
    glyph: '2',
  },
  {
    value: '3bed',
    label: '3 Bedroom',
    glyph: '3',
  },
  {
    value: '4bed',
    label: '4+ Bedrooms',
    glyph: '4',
  },
];

const houseSizeOptions: LocationDetailsWizardOption[] = [
  {
    value: '1bed',
    label: '1 Bedroom Home',
    glyph: '1',
  },
  {
    value: '2bed',
    label: '2 Bedroom Home',
    glyph: '2',
  },
  {
    value: '3bed',
    label: '3 Bedroom Home',
    glyph: '3',
  },
  {
    value: '4bed',
    label: '4+ Bedroom Home',
    glyph: '4+',
  },
];

const storageUnitSizeOptions: LocationDetailsWizardOption[] = [
  {
    value: '5x5',
    label: '5x5 (Walk-in Closet)',
    glyph: '25',
    unit: 'sqft',
  },
  {
    value: '5x10',
    label: '5x10 (Mid-size Walk-in)',
    glyph: '50',
    unit: 'sqft',
  },
  {
    value: '10x10',
    label: '10x10 (Small Bedroom)',
    glyph: '100',
    unit: 'sqft',
  },
  {
    value: '10x15',
    label: '10x15 (Large Bedroom)',
    glyph: '150',
    unit: 'sqft',
  },
  {
    value: '10x20',
    label: '10x20 (Single Car Garage)',
    glyph: '200',
    unit: 'sqft',
  },
  {
    value: '10x30',
    label: '10x30 (Double Car Garage)',
    glyph: '300',
    unit: 'sqft',
  },
];

const officeSizeOptions: LocationDetailsWizardOption[] = [
  {
    value: 'home_office',
    label: 'Home Office',
    glyph: 'H',
  },
  {
    value: 'small_office',
    label: 'Small Office (<1000 sqft)',
    glyph: 'S',
  },
  {
    value: 'medium_office',
    label: 'Medium Office (1000 - 3000 sqft)',
    glyph: 'M',
  },
  {
    value: 'large_commercial',
    label: 'Large Commercial',
    glyph: 'L',
  },
];

const otherSizeOptions: LocationDetailsWizardOption[] = [
  {
    value: 'small_load',
    label: 'Small (<1000 lbs)',
    glyph: 'S',
  },
  {
    value: 'medium_load',
    label: 'Medium (1000 - 3000 lbs)',
    glyph: 'M',
  },
  {
    value: 'large_load',
    label: 'Large (3000 - 6000 lbs)',
    glyph: 'L',
  },
  {
    value: 'xl_load',
    label: 'Extra Large (6000+ lbs)',
    glyph: 'XL',
  },
];

export const sizeOptionsByBuildingType: Partial<Record<BuildingType, LocationDetailsWizardOption[]>> = {
  'Apartment/Condo': apartmentCondoSizeOptions,
  House: houseSizeOptions,
  Townhouse: houseSizeOptions,
  Duplex: houseSizeOptions,
  Commercial: officeSizeOptions,
  'Storage Unit': storageUnitSizeOptions,
  Office: officeSizeOptions,
  Other: otherSizeOptions,
};

export const buildingTypeOptions = buildingTypes.map((bt) => ({
  value: bt,
  label: bt,
  icon: buildingTypeIconMap[bt],
}));

export const defaultAccessOptions: LocationDetailsWizardOption[] = [
  {
    value: 'none',
    label: 'Ground Floor',
    icon: 'pi pi-sign-in',
  },
  {
    value: 'stairs',
    label: 'Stairs',
    icon: 'pi pi-building',
  },
  {
    value: 'elevator',
    label: 'Elevator',
    icon: 'pi pi-arrow-circle-up',
  },
];

export const storageAccessOptions: LocationDetailsWizardOption[] = [
  {
    value: 'elevator',
    label: 'Indoors',
    icon: 'pi pi-building',
  },
  {
    value: 'outdoors',
    label: 'Outdoors',
    icon: 'pi pi-cloud',
  },
];

export const elevatorTypeOptions: LocationDetailsWizardOption[] = [
  {
    value: 'bookable',
    label: 'Bookable',
    icon: 'pi pi-check',
  },
  {
    value: 'not-bookable',
    label: 'Not Bookable',
    icon: 'pi pi-times',
  },
  {
    value: 'freight',
    label: 'Freight',
    icon: 'pi pi-box',
  },
];

export const buildSizeOptionsForBuildingType = (buildingType?: string): LocationDetailsWizardOption[] => {
  if (!buildingType) {
    return houseSizeOptions;
  }

  return sizeOptionsByBuildingType[buildingType as BuildingType] || [];
}

export const buildAccessOptionsForBuildingType = (buildingType?: string): LocationDetailsWizardOption[] => {
  if (buildingType === 'Storage Unit') {
    return storageAccessOptions;
  }

  return defaultAccessOptions;
}

export const getSizeLabelForBuildingType = (buildingType?: string): string => {
  switch (buildingType) {
    case 'House':
    case 'Townhouse':
    case 'Duplex':
    case 'Apartment/Condo':
      return 'Bedrooms';
    case 'Office':
    case 'Commercial':
      return 'Office Size';
    case 'Storage Unit':
      return 'Storage Size';
    case 'Other':
    default:
      return 'Size';
  }
}

export const selectLocationDetailsWizardPrompt = createSelector(
  locationDetailsWizardFeature.selectLocationType,
  locationDetailsWizardFeature.selectAnswers,
  locationDetailsWizardFeature.selectStep,
  (locationType, { buildingType }, step) => {
    if (!locationType) return;
    switch (step) {
      case 'buildingType':
        return locationType === 'start' ?
          `What type of building are you moving from?` :
          `What type of building are you moving into?`;
      case 'size':
        if (buildingType === 'Apartment/Condo') {
          return `How large is this apartment or condo?`
        } else if (['House', 'Townhouse', 'Duplex'].includes(buildingType)) {
          return 'How many bedrooms are there in the house?'
        } else {
          return 'How large is the space?';
        }
      case 'access':
        return 'How do we get there?'
      case 'elevatorType':
        return 'Can you tell us more about the elevators?'
      case 'floor':
        return buildingType === 'Apartment/Condo'
          ? 'What floor is the apartment or condo on?'
          : 'What floor is it on?';
    }
  }
);

const ordinal = (n: number): string =>
  n + ordinalSuffix(n);

const groundFloorOption: LocationDetailsWizardOption = {
  value: 1,
  label: 'Ground floor',
  glyph: 'G',
};

export const floorOptions: LocationDetailsWizardOption[] = [2, 3, 4, 5, 6].map((n) => ({
  value: n,
  label: n === 6 ? `${ordinal(n)} or higher` : `${ordinal(n)} floor`,
  glyph: n === 6 ? `${ordinal(n)}+` : ordinal(n),
}));

export const buildFloorOptionsForBuildingType = (buildingType?: string): LocationDetailsWizardOption[] => {
  // Requirement `#6398` - Apartment/Condo deliberately omits "Ground floor": its preceding access step has a
  // "Ground Floor" option, and the floor step is only reached when access is stairs/elevator
  // (i.e. not ground floor). Storage Unit has no such access option, so it needs it here.
  if (buildingType === 'Storage Unit') {
    return [groundFloorOption, ...floorOptions];
  }

  return floorOptions;
}

export const selectOptions = createSelector(
  locationDetailsWizardFeature.selectStep,
  locationDetailsWizardFeature.selectAnswers,
  (step, { buildingType }): LocationDetailsWizardOption[] => {

    switch (step) {
      case 'buildingType':
        return buildingTypeOptions;
      case 'size':
        return buildSizeOptionsForBuildingType(buildingType);
      case 'access':
        return buildAccessOptionsForBuildingType(buildingType);
      case 'elevatorType':
        return elevatorTypeOptions;
      case 'floor':
        return buildFloorOptionsForBuildingType(buildingType);
      default:
        return [];
    }
  }
);
